<template>
    <main class="py-12 space-y-12">
        <section>
            <div class="container lg:columns-2 gap-16">
                <h1 class="h2">
                    Maximale Haltbarkeit durch robuste Materialien
                </h1>
                <p class="article-teaser">
                    Mit der Serie 4.0 Metall setzt Sauerland Spielgeräte auf eine vollständig holzfreie Konstruktion
                    aus pulverbeschichtetem Aluminium und widerstandsfähigem HPL.
                </p>
                <p>
                    Spielgeräte im öffentlichen Raum sind dauerhaft <strong>Witterung, intensiver Nutzung und
                    mechanischer Belastung</strong> ausgesetzt. Materialien spielen daher eine zentrale Rolle für
                    die <strong>Lebensdauer und den Wartungsaufwand</strong> einer Spielanlage.
                </p>
                <p>
                    Mit der <strong>Serie 4.0 Metall</strong> verfolgt Sauerland Spielgeräte einen konsequenten
                    Ansatz: eine vollständig <strong>holzfreie Konstruktion</strong> aus pulverbeschichtetem
                    <strong>Aluminium</strong>, widerstandsfähigem <strong>HPL</strong> und langlebigen
                    Verbindungskomponenten.

                    Die Kombination dieser Materialien sorgt für <strong>robuste, wartungsarme und langlebige
                    Spielgeräte</strong>, die auch bei intensiver Nutzung über viele Jahre zuverlässig
                    funktionieren.
                </p>
                <h3>Die wichtigsten Vorteile moderner Spielplatzmaterialien</h3>
                <ul class="list-disc">
                    <li>wartungsarme Konstruktion</li>
                    <li>korrosionsbeständige Materialien</li>
                    <li>stoß- und schlagfeste Bauteile</li>
                    <li>hohe Witterungsbeständigkeit</li>
                    <li>lange Lebensdauer der Komponenten</li>
                </ul>
                <br>
                <h3>Aluminium</h3>
                <p>
                    Die tragenden Konstruktionselemente der Serie 4.0 Metall bestehen aus <strong>pulverbeschichtetem
                    Aluminium</strong>. Dieses Material bietet gegenüber klassischen Stahlkonstruktionen mehrere
                    Vorteile.
                </p>
                <p>
                    Aluminium ist <strong>korrosionsbeständig, temperaturstabil und dauerhaft formstabil</strong>.
                    Dadurch bleibt die Konstruktion auch unter schwierigen Witterungsbedingungen <strong>zuverlässig
                    und belastbar</strong>.
                </p>
                <strong>Technische Eigenschaften:</strong>
                <br>
                <br>
                <ul class="list-disc">
                    <li>Standpfosten aus Aluminiumrundrohr Ø 140 mm</li>
                    <li>Wandstärke 5 mm</li>
                    <li>pulverbeschichtet in RAL 1014 (Elfenbein)</li>
                    <li>hohe Widerstandsfähigkeit gegen Witterungseinflüsse</li>
                </ul>
                <br>
                <p>
                    Die Pulverbeschichtung <strong>schützt zusätzlich vor mechanischer Belastung</strong> und sorgt
                    für eine langlebige Oberfläche.
                </p>
                <br>
                <h3>Rutschhemmende Bodenplatten</h3>
                <p>
                    Für Standflächen und Plattformen werden HC-Strukturplatten eingesetzt. Diese Platten bestehen
                    aus <strong>mehreren Lagen harzgetränkter Zellulosefasern</strong>, die unter hohem Druck zu
                    einem <strong>robusten Material</strong> verpresst werden.
                </p>
                <p>
                    Die Oberfläche wird bereits im Produktionsprozess strukturiert und bietet dadurch eine <strong>dauerhaft
                    rutschhemmende Oberfläche</strong>.
                </p>
                <strong>Vorteile der Strukturplatten</strong>
                <br>
                <br>
                <ul class="list-disc">
                    <li>rutschhemmende Oberfläche</li>
                    <li>Rutschhemmklasse R11</li>
                    <li>wasserresistent und formstabil</li>
                    <li>kein Aufquellen durch Feuchtigkeit</li>
                </ul>
                <br>
                <p>
                    Diese Eigenschaften sorgen für <strong>sichere Standflächen</strong> auf Plattformen, Übergängen
                    und Spieltürmen.
                </p>
                <div class="relative w-full my-8 rounded-lg overflow-hidden">
                    <NuxtImg
                        alt="Vorschau einer Spielkombination der Serie 4.0 in einer Kindertagesstätte"
                        class="size-full object-cover"
                        size="100vw"
                        src="serie-4-0-metall-beispiel-kita.png"
                    />
                    <UButton
                        :ui="{ label: 'text-white font-bold', trailingIcon: 'text-white'}"
                        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        label="Zur Produktwelt Metall 4.0"
                        size="xl"
                        to="/c/serie-4.0-metall/86"
                        trailing-icon="i-lucide-chevron-right"
                    />
                </div>
                <h3>HPL-Elemente</h3>
                <p>
                    Dächer, Brüstungen und Plattenelemente bestehen aus <strong>HPL (High Pressure
                    Laminate)</strong>. Dieses Material wird aus mehreren mit Harz getränkten Papierlagen <strong>unter
                    hohem Druck und hoher Temperatur verpresst</strong>.
                </p>
                <p>
                    HPL gehört zu den <strong>widerstandsfähigsten Materialien im Außenbereich</strong> und eignet
                    sich <strong>besonders für stark beanspruchte Bauteile</strong> von Spielgeräten.
                </p>
                <strong>Eigenschaften von HPL</strong>
                <br>
                <br>
                <ul class="list-disc">
                    <li>stoß- und schlagfest</li>
                    <li>wasserfest</li>
                    <li>formstabil</li>
                    <li>kratzfest</li>
                    <li>Anti-Graffiti-fähige Oberfläche</li>
                    <li>farbstabil über viele Jahre</li>
                </ul>
                <br>
                <p>
                    Die Dekor- oder Farbschicht ist vollständig im Material einlaminiert und dadurch <strong>besonders
                    langlebig</strong>.
                </p>
                <h3>Rutschen</h3>
                <p>
                    Für Rutschen wird <strong>HPL</strong> und <strong>V2A-Edelstahl</strong> verwendet.
                    Die Seitenteile sind aus HPL, die Rutschfläche aus Edelstahl.
                    Diese Materialien sind besonders <strong>widerstandsfähig
                    gegen Witterungseinflüsse und mechanische Belastung</strong>.
                </p>
                <p>
                    Die Oberfläche sorgt für gute Rutscheigenschaften und eine <strong>hohe
                    Lebensdauer</strong> der Bauteile.
                </p>
                <strong>Eigenschaften</strong>
                <br>
                <br>
                <ul class="list-disc">
                    <li>Rutschfläche aus V2A-Edelstahl</li>
                    <li>strukturgeprägte Oberfläche</li>
                    <li>langlebig und korrosionsbeständig</li>
                    <li>witterungsbeständig</li>
                    <li>robuste Oberfläche</li>
                </ul>
                <br>
                <h3>Seile und Netze aus Herkules-Tauwerk</h3>
                <p>
                    Kletterelemente und Netzkonstruktionen bestehen aus hochwertigem Herkules-Tauwerk.
                </p>
                <p>
                    Dabei handelt es sich um <strong>Polypropylenseile mit integrierter Stahleinlage</strong>. Diese
                    Konstruktion sorgt für eine <strong>besonders hohe Belastbarkeit</strong> und eine <strong>lange
                    Lebensdauer</strong> der Seilnetze.
                </p>
                <strong>Technische Eigenschaften</strong>
                <br>
                <br>
                <ul class="list-disc">
                    <li>Polypropylenseil mit Stahleinlage</li>
                    <li>besonders hohe Zugfestigkeit</li>
                    <li>langlebige Netzverbindungen</li>
                    <li>widerstandsfähig gegen Witterung und Nutzung</li>
                </ul>
                <br>
            </div>
        </section>

        <section
            class="relative bg-cover bg-center dp-contentblock block-title py-largest dp-fsimg"
            style="background-image: url(/images/backgrounds/BQ4C0119-1.jpg);"
        >
            <div class="absolute top-0 left-0 w-full z-10 pointer-events-none">
                <BrandWave
                    :amplitude="200"
                    :frequency="1.5"
                    :height="300"
                    flip
                    flip-neutral
                    variant="neutral"
                />
            </div>
            <span class="brand-overlay is-duo is-reversed noprint"/>
            <div class="relative z-10 py-48 dp-pagebounds">
                <h2 class=" my-none py-large py-l-largest text-center text-white! js-fade-in is-added is-visible">
                    Nachhaltigkeit durch Aluminiumrecycling
                </h2>
            </div>
            <div class="absolute bottom-0 left-0 w-full z-10 pointer-events-none">
                <BrandWave
                    :amplitude="200"
                    :frequency="1"
                    :height="400"
                    variant="neutral"
                />
            </div>
        </section>

        <section>
            <div class="container lg:columns-2 gap-16">
                <NuxtImg
                    alt="Schematisches 3D-Modell einer Spielkombination der Serie Metall 4.0 das die einzelnen Bauteile separiert abbildet"
                    height="427"
                    src="spielkombi-exp.png"
                    width="480"
                />
                <h3>
                    Aluminium ist eines der am besten recycelbaren Materialien der Industrie.
                </h3>
                <p>
                    Das Material kann nahezu vollständig wiederverwertet werden, <strong>ohne
                    Qualitätsverlust</strong>. Beim Recycling werden nur etwa 5 % der Energie benötigt, die bei der
                    Herstellung von Primäraluminium erforderlich ist.
                </p>
                <p>
                    Dadurch leistet Aluminium einen wichtigen Beitrag zur <strong>Ressourcenschonung und
                    Kreislaufwirtschaft</strong>.
                </p>
                <p>
                    In Deutschland werden bereits heute große Mengen Aluminium recycelt und wiederverwendet –
                    insbesondere in der Bau- und Verkehrsindustrie.
                </p>
                <br>
                <h2>Warum langlebige Materialien im Spielplatzbau wichtig sind</h2>
                <p>
                    Spielplätze sind <strong>langfristige</strong> Investitionen. Betreiber wie Kommunen,
                    Wohnungsbaugesellschaften oder Bildungseinrichtungen benötigen Anlagen, die <strong>über viele
                    Jahre zuverlässig funktionieren</strong>.
                </p>
                <p>Die Auswahl der richtigen Materialien entscheidet dabei maßgeblich über:</p>
                <ul class="list-disc">
                    <li>Wartungsaufwand</li>
                    <li>Betriebskosten</li>
                    <li>Lebensdauer der Anlage</li>
                    <li>Nachhaltigkeit der Konstruktion</li>
                </ul>
                <br>
                <p>
                    <strong>Mit der Serie 4.0 Metall setzt Sauerland Spielgeräte bewusst auf Materialien, die diesen
                        Anforderungen langfristig gerecht werden.</strong>
                </p>
            </div>
        </section>

        <section>
            <div class="container">
                <article class="dp-article">
                    <div class="article-text">
                        <h3 class="h3 title">
                            Serie 4.0 Metall im Video
                        </h3>
                        <div class="grid lg:grid-cols-2 gap-8">
                            <div class="dp-video js-fade-in is-added is-visible">
                                <VideoPlayer
                                    url="/videos/Sauerland-Spielgeraete 4-0Metall 03-2026_720p.mp4"
                                    name="Sauerland 2026 Metall 4.0"
                                />
                            </div>
                        </div>
                    </div>
                </article>
            </div>
        </section>

        <section>
            <Carousel/>
        </section>
    </main>
</template>

<script lang="ts" setup>
import VideoPlayer from '~/components/VideoPlayer.vue';

definePageMeta({
    path: '/Material/Metall-4-0',
});

const header = useState<{ title: string; subTitle: string }>(
    'header',
);
header.value = {
    title: 'Eine Welt',
    subTitle: 'von Dauer',
};
</script>

<style scoped>

</style>
